import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingSpinner from "./LoadingSpinner";
import '../index.css';
import { getSkinSrc } from './SeasonalSkins';

function Marketplace({ user, setUser, onNavigate, setSelectedCardId }) {
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);

  // UI state
  const [query, setQuery] = useState('');
  const [rarity, setRarity] = useState('all');     // all | Common | Rare | Epic | Legendary
  const [side, setSide] = useState('all');         // all | good | bad
  const [sort, setSort] = useState('price_asc');   // price_asc | price_desc | newest
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  useEffect(() => {
  if (user?.side === 'good' || user?.side === 'bad') {
    setSide(user.side);
  }
}, [user?.side]);


  const MAX_LEVEL = 10;

  // Buy modal
  const [buyOpen, setBuyOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  // Fee (percent) — we only show/use it if we *know* it
  const [feePct, setFeePct] = useState(0);
  const [feeLoaded, setFeeLoaded] = useState(false); // controls whether to show fee UI

  // Error modal
  const [errOpen, setErrOpen] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const [successOpen, setSuccessOpen] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);

  // Abilities (for icons)
  const ABILITIES = useMemo(
    () => [
      'Berserk', 'Pierce', 'Frenzy', 'Bleed', 'Flameburst', 'Execution',
      'Shield Wall', 'Regen', 'Thorns', 'Aegis', 'Purify', 'Fortify'
    ],
    []
  );

  // ---- helpers ----
  const SIDE_ALIASES = useMemo(() => ({
    good: new Set(['good', 'sun', 'light', 'hero', 'order']),
    bad:  new Set(['bad', 'moon', 'dark', 'villain', 'chaos']),
  }), []);

  const canonicalSide = (s) => {
    const v = String(s || '').toLowerCase();
    if (SIDE_ALIASES.good.has(v)) return 'good';
    if (SIDE_ALIASES.bad.has(v))  return 'bad';
    return v; // pass through 'neutral' or unknowns if any
  };

  const deriveSideFromCharacter = (ch) => {
    if (!ch || typeof ch !== 'object') return '';
    let good = 0, bad = 0, last = '';
    for (const part of Object.values(ch)) {
      const canon = canonicalSide(part?.side);
      if (canon) last = canon;
      if (canon === 'good') good++;
      else if (canon === 'bad') bad++;
    }
    if (good === 0 && bad === 0) return last; // only unknowns seen
    return good >= bad ? 'good' : 'bad';
  };

  const parseAbilities = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return []; }
  };

  // For ICONS ONLY – keep using the curated set
  const sanitizeAbilityListForIcons = (list) =>
    (list || [])
      .map((a) => (typeof a === 'string' ? a : (a?.name || '')))
      .map((s) => String(s).trim())
      .filter((s) => s && ABILITIES.includes(capFirst(s)));

  // For CAP CHECK – count abilities even if IDs don’t match curated names
  const abilityCountForCap = (rawList) => {
    const list = Array.isArray(rawList) ? rawList : [];
    let total = 0;
    for (const a of list) {
      if (typeof a === 'string') {
        if (a.trim()) total++;
      } else if (a && typeof a === 'object' && String(a.name || '').trim()) {
        total++;
      }
    }
    return total;
  };

  const capFirst = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

  const getSide = (c) => {
    const explicit = canonicalSide(c?.side);
    const fromParts = deriveSideFromCharacter(c?.character);
    return explicit || fromParts || '';
  };

  const isOppositeSideCard = (card) => {
  if (!card) return false;

  const userSide = canonicalSide(user?.side);
  const cardSide = getSide(card);

  if (!userSide || !cardSide) return false;

  return userSide !== cardSide;
};

  // --- Rarity caps (match Character/Fight) + evolve eligibility helpers ---
 const RARITY_PROFILES = useMemo(() => ({
  Common:    { atk: [2, 5],  def: [1, 4],  hp: [14, 22], abilities: [0, 1] },
  Rare:      { atk: [3, 6],  def: [2, 5],  hp: [18, 28], abilities: [1, 1] },
  Epic:      { atk: [5, 8],  def: [3, 7],  hp: [24, 36], abilities: [1, 2] },
  Legendary: { atk: [7, 10], def: [5, 9],  hp: [30, 44], abilities: [2, 3] }
}), []);

  const normRarity = (r) => String(r || 'Common').toLowerCase();
  const pickProfile = (rarity) => {
    const key = normRarity(rarity);
    if (key === 'common')    return RARITY_PROFILES.Common;
    if (key === 'rare')      return RARITY_PROFILES.Rare;
    if (key === 'epic')      return RARITY_PROFILES.Epic;
    if (key === 'legendary') return RARITY_PROFILES.Legendary;
    return RARITY_PROFILES.Common;
  };

  const canCardImproveByStats = (card) => {
    const prof = pickProfile(card?.rarity);
    return (
      (card?.attack  ?? 0) < prof.atk[1] ||
      (card?.defense ?? 0) < prof.def[1] ||
      (card?.health  ?? 0) < prof.hp[1]
    );
  };

  const canCardImprove = (card, abilityCount) => {
    if (!card) return false;
    const prof = pickProfile(card.rarity);
    const canStat = canCardImproveByStats(card);
    const [, maxAbilities] = prof.abilities;
    const canAb = abilityCount < maxAbilities;
    return canStat || canAb;
  };

  // Parse any fee format: 5, "5", "5%", 0.05, "0.05"
  function parseFeePercent(raw) {
    if (raw == null) return null;
    const s = String(raw).trim();
    const num = Number(s.replace('%', ''));
    if (!Number.isFinite(num)) return null;
    // If <= 1, treat as decimal (0.05 -> 5%)
    const pct = num <= 1 ? Math.round(num * 100 * 100) / 100 : num;
    if (pct < 0) return 0;
    return pct;
  }

  // Load all cards for sale
  const loadMarket = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/cards/for-sale`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to fetch marketplace');

      // Server returns { items: [...] }. Support { cards: [...] } as a fallback.
      const items = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.cards)
          ? data.cards
          : [];
      setCards(items);
    } catch (e) {
      setErrMsg(e.message || 'Failed to load marketplace');
      setErrOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // On mount: try to hydrate fee from localStorage cache (no network)
  useEffect(() => {
    const cached = localStorage.getItem('marketplaceFeePct');
    const parsed = parseFeePercent(cached);
    if (parsed != null) {
      setFeePct(parsed);
      setFeeLoaded(true);
    }
  }, []);

  useEffect(() => { loadMarket(); }, []);

  // Client-side filtering/sorting
  const filtered = useMemo(() => {
    let list = Array.isArray(cards) ? [...cards] : [];

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((c) => (c.name || '').toLowerCase().includes(q));
    }

    if (rarity !== 'all') {
      list = list.filter(
        (c) => String(c.rarity || '').toLowerCase() === rarity.toLowerCase()
      );
    }

    if (side !== 'all') {
      list = list.filter((c) => getSide(c) === side.toLowerCase());
    }

    // price range — treat empty input as no bound
    const min = minPrice === '' ? null : Number(minPrice);
    const max = maxPrice === '' ? null : Number(maxPrice);
    if (min !== null && Number.isFinite(min)) {
      list = list.filter((c) => Number(c.sale_price) >= min);
    }
    if (max !== null && Number.isFinite(max)) {
      list = list.filter((c) => Number(c.sale_price) <= max);
    }

    if (sort === 'price_asc') {
      list.sort((a, b) => Number(a.sale_price) - Number(b.sale_price));
    } else if (sort === 'price_desc') {
      list.sort((a, b) => Number(b.sale_price) - Number(a.sale_price));
    } else if (sort === 'newest') {
      list.sort((a, b) => {
        const at = new Date(a.listed_at || a.updated_at || a.created_at || 0).getTime();
        const bt = new Date(b.listed_at || b.updated_at || b.created_at || 0).getTime();
        return bt - at;
      });
    }

    return list;
  }, [cards, query, rarity, side, sort, minPrice, maxPrice]);

  const openBuy = (card) => { setSelectedCard(card); setBuyOpen(true); };
  const closeBuy = () => { setBuyOpen(false); setSelectedCard(null); };

  const buyCard = async () => {
    if (!selectedCard) return;
    try {
      setBuying(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cards/${selectedCard.id}/buy`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ expectedPrice: Number(selectedCard.sale_price) }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to buy this card');

      // Remove from list
      setCards((prev) => prev.filter((c) => c.id !== selectedCard.id));

      // Update user balances from server; fallback subtract expected currency
      const price = Number(selectedCard.sale_price) || 0;
      const currency = String(selectedCard.sale_currency || 'sparks').toLowerCase();
      setUser((prev) => ({
        ...prev,
        runes:
          typeof data?.balances?.runes === 'number'
            ? data.balances.runes
            : currency === 'runes' && typeof prev?.runes === 'number'
              ? Math.max(0, prev.runes - price)
              : prev?.runes,
        sparks:
          typeof data?.balances?.sparks === 'number'
            ? data.balances.sparks
            : currency === 'sparks' && typeof prev?.sparks === 'number'
              ? Math.max(0, prev.sparks - price)
              : prev?.sparks,
      }));

      // Authoritative fee from server (cards.js returns feePercent)
      if (typeof data?.feePercent === 'number') {
        const parsed = parseFeePercent(data.feePercent);
        if (parsed != null) {
          setFeePct(parsed);
          setFeeLoaded(true);
          try {
            localStorage.setItem('marketplaceFeePct', String(parsed));
          } catch {}
        }
      }

      // 🎉 Success: show celebratory modal
      setSuccessInfo({
        id: selectedCard.id,
        name: selectedCard.name,
        price,
        currency: currency.toUpperCase()
      });
      setSuccessOpen(true);

      closeBuy();
    } catch (e) {
      setErrMsg(e.message || 'Purchase failed');
      setErrOpen(true);
    } finally {
      setBuying(false);
    }
  };

  const rarityOptions = ['all', 'Common', 'Rare', 'Epic', 'Legendary'];
  const sideOptions = ['all', 'good', 'bad'];

  const SIDE_LABELS = useMemo(() => ({
  all: 'All Sides',
  good: 'Sun Pharaohs',
  bad: 'Moon Anubis',
}), []);

  return (
  <div className="shop-container">
    <div style={{ marginLeft: -10, marginRight: -10, marginTop: -13 }}>
      {loading ? (
        <div
          style={{
            minHeight: 430,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LoadingSpinner text="Loading Marketplace" size={64} />
        </div>
      ) : (
        <>
          <div className="modal_title">
            MeerkatPlace
          </div>

      {/* FILTERS */}
      <div
        className="modal_body modal_body_2"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 8,
          boxSizing: "border-box",
          marginBottom: 10,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        <select
          className="form-select"
          value={rarity}
          onChange={(e) => setRarity(e.target.value)}
          style={{
            width: "100%",
            minWidth: 0,
          }}
        >
          {rarityOptions.map((r) => (
            <option key={r} value={r}>
              {r === "all" ? "All Rarities" : r}
            </option>
          ))}
        </select>

        <select
          className="form-select"
          value={side}
          onChange={(e) => setSide(e.target.value)}
          style={{
            width: "100%",
            minWidth: 0,
          }}
        >
          {sideOptions.map((s) => (
            <option key={s} value={s}>
              {SIDE_LABELS[s] || s}
            </option>
          ))}
        </select>

        <select
          className="form-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          style={{
            width: "100%",
            minWidth: 0,
          }}
        >
          <option value="price_asc">Price ↑</option>
          <option value="price_desc">Price ↓</option>
          <option value="newest">Newest</option>
        </select>

        <button
          type="button"
          className="game-button-small yellow-button"
          onClick={loadMarket}
          style={{
            width: "100%",
            fontSize: 15,
            fontWeight: 600,
            height: 37,
          }}
        >
          RELOAD
        </button>
      </div>

      {/* EMPTY */}
      {!loading && filtered.length === 0 && (
        <div class="modal_body">
        <div
          className="shadow1"
          style={{
            padding: "18px 12px",
            boxSizing: "border-box",
            borderRadius: 10,
            background: "rgba(0,0,0,0.85)",
            color: "#FFC97F",
            fontSize: 21,
            textAlign: "center",
          }}
        >
          Nothing listed yet.
        </div></div>
      )}

      {/* CARDS GRID */}
      {!loading && filtered.length > 0 && (
        <div
          className="modal_body modal_body_2"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 10,
            boxSizing: "border-box",
            paddingBottom: 10,
            paddingTop: 15,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          {filtered.map((card, index) => {
            const rawAbilities = parseAbilities(card.abilities);

            const abilitiesForIcons = sanitizeAbilityListForIcons(
              rawAbilities.map((a) =>
                typeof a === "string"
                  ? capFirst(a)
                  : a?.name
                    ? capFirst(String(a.name))
                    : ""
              )
            );

            const price = Number(card.sale_price) || 0;
            const currency = String(card.sale_currency || "sparks").toLowerCase();
            const currencyIcon = currency === "runes" ? "/runes.png" : "/sparks.png";

            const isMaxedLevel =
              (card?.evolvesLeft != null && Number(card.evolvesLeft) <= 0) ||
              Number(card?.level || 1) >= MAX_LEVEL;

            return (
              <button
                key={card.id || index}
                type="button"
                onClick={() => {
                  setSelectedCard(card);
                  setBuyOpen(true);
                }}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  position: "relative",
                  overflow: "hidden",
                  cursor: "pointer",
                  borderRadius: 0,
                  backgroundColor: "transparent",
                  backgroundImage: 'url("/v2/item.png")',
                  backgroundRepeat: "repeat",
                  backgroundSize: "100% 100%",
                  backgroundPosition: "center center",
                  border: 0,
                  padding: 3,
                }}
              >
                {/* CARD PREVIEW */}
                <div
                  style={{
                    float: "left",
                    width: "calc(100% - 4px)",
                    aspectRatio: "1 / 1",
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 8,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    marginTop: 2,
                    marginLeft: 2,
                    marginRight: 2,
                  }}
                >
                  {/* LEVEL */}
                  <div
                    style={{
                      position: "absolute",
                      top: 6,
                      left: 5,
                      width: 28,
                      height: 28,
                      backgroundImage: "url('/v2/position.png')",
                      backgroundSize: "100% 100%",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      color: "#fff",
                      fontSize: 15,
                      lineHeight: "28px",
                      textAlign: "center",
                      zIndex: 20,
                      fontFamily: '"Fredoka", sans-serif',
                      fontWeight: 600,
                    }}
                  >
                    {card.level ?? 1}
                  </div>

                  {/* ABILITIES */}
                  <div
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 5,
                      zIndex: 30,
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                      pointerEvents: "none",
                    }}
                  >
                    {abilitiesForIcons.map((ability, idx) => {
                      const abilityKey = String(ability || "").toLowerCase();

                      return (
                        <div
                          key={`${ability}-${idx}`}
                          style={{
                            width: 16,
                            height: 16,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          title={ability}
                        >
                          <img
                            src={`/a_${abilityKey}.png`}
                            alt={ability}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                            style={{
                              width: 21,
                              height: 21,
                              objectFit: "contain",
                              pointerEvents: "none",
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* EVOLVE BADGE */}
                  <div
                    className="shadow1"
                    style={{
                      position: "absolute",
                      left: 38,
                      top: 7,
                      right: 28,
                      zIndex: 25,
                      height: 20,
                      borderRadius: 5,
                      background: isMaxedLevel ? "#7b1d16" : "#267b22",
                      color: "#fff",
                      fontSize: 10,
                      lineHeight: "20px",
                      fontWeight: 700,
                      textAlign: "center",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isMaxedLevel ? "MAXED" : "EVOLVE"}
                  </div>

                  {/* CHARACTER */}
                  <div
                    className="char_bg"
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      zIndex: 2,
                    }}
                  >
                    {Object.entries(card.character || {})
                      .sort(([a], [b]) => {
                        const order = [
                          "background",
                          "body",
                          "boots",
                          "pants",
                          "shirt",
                          "neck",
                          "face",
                          "eyes",
                          "mouth",
                          "head",
                          "weapons",
                        ];

                        const indexA = order.includes(a) ? order.indexOf(a) : order.length;
                        const indexB = order.includes(b) ? order.indexOf(b) : order.length;

                        return indexA - indexB;
                      })
                      .map(([part, data]) => {
                        if (!data || typeof data !== "object") return null;

                        const defaultSrc = `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

                        const seasonName =
                          card?.seasonal_accessory?.season_name ||
                          card?.season_name ||
                          null;

                        const src = getSkinSrc(seasonName, part, defaultSrc);

                        const zIndexMap = {
                          background: 1,
                          body: 2,
                          boots: 3,
                          pants: 4,
                          shirt: 5,
                          neck: 6,
                          face: 7,
                          eyes: 8,
                          mouth: 9,
                          head: 10,
                          weapons: 11,
                        };

                        return (
                          <img
                            key={part}
                            src={src}
                            alt=""
                            draggable={false}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                            style={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              zIndex: zIndexMap[part] ?? 10,
                              pointerEvents: "none",
                            }}
                          />
                        );
                      })}
                  </div>

                  {/* RARITY */}
                  <div
                    className="shadow1"
                    style={{
                      zIndex: 35,
                      textTransform: "uppercase",
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: "100%",
                      color: "#fff",
                      height: 18,
                      fontSize: 11,
                      lineHeight: "21px",
                      textAlign: "center",
                      fontWeight: 600,
                      background: "rgba(77, 44, 36, 0.5)",
                    }}
                  >
                    {card.rarity || "common"}
                  </div>
                </div>

                {/* STATS */}
                <div
                  style={{
                    float: "left",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 4px",
                    boxSizing: "border-box",
                    height: 25,
                  }}
                >
                  <div
                    className="shadow1"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <img src="/s_attack.png" alt="" style={{ width: 12, height: 12 }} />
                    {card.attack ?? 0}
                  </div>

                  <div
                    className="shadow1"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <img src="/s_defense.png" alt="" style={{ width: 12, height: 12 }} />
                    {card.defense ?? 0}
                  </div>

                  <div
                    className="shadow1"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <img src="/s_health.png" alt="" style={{ width: 12, height: 12 }} />
                    {card.health ?? 0}
                  </div>
                </div>

                {/* PRICE */}
                <div
                  style={{
                    float: "left",
                    margin: 3,
                    width: "calc(100% - 6px)",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                    color: "#fff",
                    fontSize: 16,
                    fontWeight: 600,
                    background: "#7e4123",
                    lineHeight: "24px",
                    marginTop: 0,
                    marginBottom: 6,
                  }}
                >
                  <img
                    src={currencyIcon}
                    alt=""
                    draggable={false}
                    style={{
                      width: 21,
                      height: 21,
                      objectFit: "contain",
                    }}
                  />

                  <span className="shadow1">{price}</span>
                </div>

                {/* SELLER */}
                {card.seller_name && (
                  <div
                    className="shadow1"
                    style={{
                      float: "left",
                      width: "100%",
                      color: "rgba(255,255,255,0.75)",
                      fontSize: 11,
                      fontWeight: 600,
                      textAlign: "center",
                      paddingBottom: 12,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Seller: {card.seller_name}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="modal_footer" style={{marginTop: -10}}>

      <button
  style={{ marginRight: 10 }}
  type="button"
  className="game-button green-button"
  onClick={() => onNavigate?.("shop", { initialView: "cards" })}
>
  BUY NEW CARDS
</button>

        <button
          type="button"
          className="game-button red-button"
          onClick={() => onNavigate("shop")}
        >
          BACK
        </button>
      </div>

       </>
      )}
    </div>



    {/* BUY MODAL */}
    <AnimatePresence>
      {buyOpen && selectedCard && (
        <div
          className="modal_box"
        >
          <div className="modal_title">
            Confirm?
            <button
              type="button"
              className="modal_close"
              onClick={() => {
                setBuyOpen(false);
                setSelectedCard(null);
              }}
            />
          </div>

          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
            <div className="modal_inside">
            <img src="/finger.png" style={{height: 80}}/>
              <div
                className="shadow2"
                style={{
                  fontSize: 28,
                  fontWeight: 600,
                  marginBottom: 10,
                  marginTop: 10,
                }}
              >
                Confirm Purchase?
              </div>

              <div
                className="info shadow1"
                style={{
                  fontSize: 21,
                  color: "#ddd",
                  lineHeight: "28px",
                }}
              >
                Buy{" "}
                <strong style={{ color: "#09BDFF", textTransform: "capitalize" }}>
                  {selectedCard.name}
                </strong>
                <br />
                for{" "}
                <span style={{ color: "#ffbb01", fontWeight: "bold" }}>
                  {Number(selectedCard.sale_price) || 0}{" "}
                  {String(selectedCard.sale_currency || "sparks").toUpperCase()}
                </span>
                ?
              </div>

              {isOppositeSideCard(selectedCard) && (
  <div
    className="info"
    style={{
      boxSizing: "border-box",
      marginTop: 18,
      padding: "12px 14px",
      borderRadius: 8,
      background: "rgba(160, 35, 25, 0.9)",
      border: "2px solid #ff725f",
      color: "#fff",
      fontSize: 16,
      lineHeight: "22px",
      textAlign: "center",
      fontWeight: 600,
    }}
  >
    ⚠️ OPPOSITE SIDE CARD
    <div
      style={{
        marginTop: 5,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: "19px",
      }}
    >
      This card belongs to{" "}
      <strong>
        {getSide(selectedCard) === "good"
          ? "Sun Pharaohs"
          : "Moon Anubis"}
      </strong>
      .
      <br />
      You can buy it, but you cannot use it while playing on your current side.
    </div>
  </div>
)}

              <div
                className="shadow1"
                style={{
                  float: "left",
                  width: "100%",
                  fontSize: 16,
                  color: "#fff",
                  opacity: 0.85,
                  marginTop: 28,
                }}
              >
                Evolution:{" "}
                {(() => {
                  const isMaxedLevel =
                    (selectedCard?.evolvesLeft != null &&
                      Number(selectedCard.evolvesLeft) <= 0) ||
                    Number(selectedCard?.level || 1) >= MAX_LEVEL;

                  return (
                    <b style={{ color: isMaxedLevel ? "#ffdf5f" : "#9adcfb" }}>
                      {isMaxedLevel ? "MAXED" : "Available"}
                    </b>
                  );
                })()}
              </div>
            </div>

            <div className="modal_footer">
              <button
                type="button"
                className="game-button green-button"
                disabled={buying}
                onClick={buyCard}
                style={{
                  marginRight: 10,
                  opacity: buying ? 0.6 : 1,
                  cursor: buying ? "not-allowed" : "pointer",
                }}
              >
                {buying ? "Buying..." : "Confirm"}
              </button>

              <button
                type="button"
                className="game-button red-button"
                disabled={buying}
                onClick={() => {
                  setBuyOpen(false);
                  setSelectedCard(null);
                }}
                style={{
                  opacity: buying ? 0.6 : 1,
                  cursor: buying ? "not-allowed" : "pointer",
                }}
              >
                CANCEL
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>

    {/* SUCCESS MODAL */}
    <AnimatePresence>
      {successOpen && successInfo && (
        <div
          className="modal_box"
        >
          <div className="modal_title">
            Success
            <button
              type="button"
              className="modal_close"
              onClick={() => setSuccessOpen(false)}
            />
          </div>

          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
            <div className="modal_inside">
              <img
                src="/finger.png"
                alt=""
                style={{
                  height: 80,
                  marginBottom: 10,
                }}
              />

              <div
                className="shadow2"
                style={{
                  fontSize: 28,
                  fontWeight: 600,
                  marginBottom: 10,
                }}
              >
                Purchase Complete!
              </div>

              <p
                className="info shadow1"
                style={{
                  fontSize: 20,
                  color: "#ddd",
                  lineHeight: "26px",
                }}
              >
                You bought{" "}
                <strong style={{ color: "#09BDFF" }}>
                  {successInfo.name}
                </strong>
                <br />
                for{" "}
                <span style={{ color: "#ffbb01", fontWeight: "bold" }}>
                  {successInfo.price} {successInfo.currency}
                </span>
                .
              </p>
            </div>

            <div className="modal_footer">
              <button
                type="button"
                className="game-button green-button"
                style={{ marginRight: 10 }}
                onClick={() => {
                  setSuccessOpen(false);
                  setSelectedCardId?.(successInfo.id);
                  onNavigate?.("inventoryHub");
                }}
              >
                INVENTORY
              </button>

              <button
                type="button"
                className="game-button red-button"
                onClick={() => setSuccessOpen(false)}
              >
                CLOSE
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>

    {/* ERROR MODAL */}
    <AnimatePresence>
      {errOpen && (
        <div
          className="modal_box"         
        >
          <div className="modal_title">
            Error
            <button
              type="button"
              className="modal_close"
              onClick={() => {
                setErrOpen(false);
                setErrMsg("");
              }}
            />
          </div>

          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
            <div className="modal_inside">
              <img
                src="/finger.png"
                alt=""
                style={{
                  height: 80,
                  marginBottom: 10,
                  transform: "rotate(180deg)",
                }}
              />

              <div
                className="shadow2"
                style={{
                  fontSize: 28,
                  fontWeight: 600,
                  marginBottom: 10,
                }}
              >
                Oops!
              </div>

              <p
                className="info shadow1"
                style={{
                  fontSize: 18,
                  color: "#ddd",
                  lineHeight: "26px",
                  textTransform: "capitalize"
                }}
              >
                {errMsg}
              </p>
            </div>

            <div className="modal_footer">
              <button
                type="button"
                className="game-button red-button"
                onClick={() => {
                  setErrOpen(false);
                  setErrMsg("");
                }}
              >
                CLOSE
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  </div>
);
}

export default Marketplace;
